import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ShoppingCart, ArrowRight } from "lucide-react";
import { Button } from "@cieda/djayma-ui";
import { StatusBadge } from "@/components/admin/shared";
import type { RecentOrder } from "@/lib/admin-api";

// ---------------------------------------------------------------------------
// FCFA formatter
// ---------------------------------------------------------------------------

const FCFA_FMT = new Intl.NumberFormat("fr-SN", {
  style: "decimal",
  maximumFractionDigits: 0,
});

function formatFCFA(amount: number): string {
  return `${FCFA_FMT.format(amount)} FCFA`;
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const day = String(d.getDate()).padStart(2, "0");
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const hours = String(d.getHours()).padStart(2, "0");
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${day}/${month} ${hours}:${minutes}`;
}

const PAYMENT_LABELS: Readonly<Record<string, string>> = {
  wave: "Wave",
  orange_money: "Orange Money",
  carte: "Carte bancaire",
  especes: "Espèces",
  virement: "Virement",
};

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface RecentOrdersWidgetProps {
  readonly orders: ReadonlyArray<RecentOrder>;
  readonly isLoading?: boolean;
}

// ---------------------------------------------------------------------------
// Skeleton
// ---------------------------------------------------------------------------

function WidgetSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="flex items-center justify-between rounded-lg border border-stone-100 px-4 py-3"
        >
          <div className="space-y-1.5">
            <div className="h-3.5 w-24 animate-pulse rounded bg-stone-100" />
            <div className="h-3 w-32 animate-pulse rounded bg-stone-100" />
          </div>
          <div className="h-5 w-16 animate-pulse rounded-full bg-stone-100" />
        </div>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RecentOrdersWidget({
  orders,
  isLoading,
}: RecentOrdersWidgetProps) {
  const { t } = useTranslation(["admin", "dashboard"]);
  const navigate = useNavigate();

  return (
    <div className="rounded-lg border border-stone-200 bg-white shadow-sm">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-stone-100 px-5 py-4">
        <div className="flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
            <ShoppingCart className="h-4 w-4" />
          </div>
          <h3 className="font-display text-base font-semibold text-stone-900">
            {t("dashboard.recent_orders_title", "Commandes récentes")}
          </h3>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate("/admin/commerce/commandes")}
          className="gap-1.5 text-xs text-stone-500 hover:text-stone-700"
        >
          {t("dashboard.view_all_orders", "Voir tout")}
          <ArrowRight className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Content */}
      <div className="px-5 py-4">
        {isLoading ? (
          <WidgetSkeleton />
        ) : orders.length === 0 ? (
          <div className="py-8 text-center text-sm text-stone-400">
            {t(
              "dashboard.no_recent_orders",
              "Aucune commande récente.",
            )}
          </div>
        ) : (
          <div className="space-y-1">
            {orders.map((order) => (
              <button
                key={order.id}
                onClick={() => navigate(`/admin/orders/${order.id}`)}
                className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-stone-50"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-stone-800">
                      {order.public_ref}
                    </span>
                    <StatusBadge status={order.status} variant="order" />
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-xs text-stone-500">
                    <span>{order.guest_name ?? "Client"}</span>
                    <span aria-hidden="true">·</span>
                    <span>
                      {PAYMENT_LABELS[order.payment_method] ??
                        order.payment_method}
                    </span>
                    <span aria-hidden="true">·</span>
                    <span className="tabular-nums">
                      {formatDate(order.created_at)}
                    </span>
                  </div>
                </div>
                <span className="ml-4 shrink-0 text-sm font-semibold tabular-nums text-stone-900">
                  {formatFCFA(order.total_ttc_fcfa)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
